import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../common/Button";
import Logo from "../../common/Logo";
import GlassCard from "../../common/marketing/GlassCard";
import SectionLabel from "../../common/marketing/SectionLabel";
import SerifAccent from "../../common/marketing/SerifAccent";

// Public page an invited department lands on from the trial invitation email.
// It is the web version of the "Trial participation requirements" PDF; the
// Agree button records the acceptance and forwards to agency signup with the
// same token, where the seats and expiry are granted automatically.

interface InviteInfo {
  email: string;
  officerSeats: number;
  investigatorSeats: number;
  trialDays: number;
}

interface Item {
  label: string;
  title: string;
  body: string;
}

const REQUIREMENTS: Item[] = [
  {
    label: "Progress reviews",
    title: "Bi-weekly Zoom meetings",
    body: "Review progress, receive feedback, and address any questions or concerns regarding the trial with a command staff member.",
  },
  {
    label: "Communication",
    title: "Command staff alignment",
    body: "Maintain consistent communication between the designated point of contact and an assigned member of command staff.",
  },
  {
    label: "Point of contact",
    title: "Primary coordinator",
    body: "Designate a primary point of contact to coordinate communication and logistics throughout the trial.",
  },
  {
    label: "Trial usage",
    title: "Mandate usage for selected trial group",
    body: "To respect both parties' time, we ask the department to mandate usage among the selected trial group. Consistent use helps produce objective data on whether the software is a good fit.",
  },
];

const SAFEGUARDS: Item[] = [
  {
    label: "Legal review",
    title: "District Attorney coordination",
    body: "Coordinate with the District Attorney's Office, or equivalent legal authority, at least once during the trial period to evaluate any legal or procedural considerations raised by AI-assisted report writing.",
  },
  {
    label: "Security & compliance",
    title: "Protected trial environment",
    body: "Trial activity is supported by CJIS-compliant controls, AWS GovCloud hosting, MFA + SSO (SAML/OIDC), 256-bit encryption, and full audit trails.",
  },
  {
    label: "Officer authorship",
    title: "Review and sign-off",
    body: "Every AI-generated draft is opened for officer review, editing, and approval before filing. During the trial, the officer remains the final author of the document of record.",
  },
  {
    label: "Auditability",
    title: "Verifiable activity history",
    body: "Edits, signatures, exports, and access events are logged automatically, giving the department a reviewable history of activity generated during the trial.",
  },
];

const PROVIDES: Item[] = [
  {
    label: "Training & support",
    title: "On-demand training and troubleshooting",
    body: "Support for all officers participating in the trial.",
  },
  {
    label: "Adoption visibility",
    title: "Weekly usage and adoption updates",
    body: "Regular updates covering participating officers and trial activity.",
  },
  {
    label: "Best practices",
    title: "AI usage documentation",
    body: "Best practices documentation for effective AI usage.",
  },
  {
    label: "Peer reference",
    title: "Department introductions",
    body: "Direct contact with departments that have fully integrated our solution.",
  },
];

const ItemCard: React.FC<{ item: Item }> = ({ item }) => (
  <GlassCard interactive={false} className="p-5">
    <p className="font-display text-[11px] font-semibold uppercase tracking-[0.1em] text-blue-500 m-0">
      {item.label}
    </p>
    <h3 className="font-display text-base font-semibold text-slate-900 mt-1 mb-1">
      {item.title}
    </h3>
    <p className="text-sm leading-relaxed text-slate-600 m-0">{item.body}</p>
  </GlassCard>
);

const SectionHeading: React.FC<{ n: string; title: string; intro: string }> = ({
  n,
  title,
  intro,
}) => (
  <div className="mb-5">
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 text-sm font-semibold text-white">
        {n}
      </span>
      <h2 className="font-display text-2xl font-bold text-slate-900 m-0">{title}</h2>
    </div>
    <p className="mt-2 ml-11 text-sm text-slate-500 m-0">{intro}</p>
  </div>
);

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const TrialRequirements: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError("This page needs the link from your trial invitation email.");
      setLoading(false);
      return;
    }
    axios
      .get("/api/agency/trial-invitations/validate", { params: { token } })
      .then((res) => !cancelled && setInfo(res.data))
      .catch(
        (err) =>
          !cancelled &&
          setError(err?.response?.data?.error || "We could not open this invitation."),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const agree = async () => {
    setSubmitting(true);
    try {
      await axios.post("/api/agency/trial-invitations/agree", { token });
      navigate(`/agency/signup?trialInvite=${encodeURIComponent(token)}`);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const seatSummary = info
    ? [
        plural(info.officerSeats, "officer seat"),
        info.investigatorSeats > 0 ? plural(info.investigatorSeats, "investigator seat") : null,
      ]
        .filter(Boolean)
        .join(" and ")
    : "";

  const steps = [
    {
      title: "Create your agency management account",
      body: info
        ? `Agree below to register your department. Your trial seats (${seatSummary}) are added automatically and your ${info.trialDays} days begin when you finish signing up.`
        : "Agree below to register your department. Your trial seats are added automatically.",
    },
    {
      title: "Invite your officers",
      body: "In the Agency Management Portal, confirm your seats and email invitations to your selected officers. A short guided tour walks you through it the first time you sign in.",
    },
    {
      title: "Have each officer register from their invitation",
      body: "Each officer should open the invitation email and use its unique registration link to create an account.",
    },
    {
      title: "Coordinate training and begin evaluation",
      body: "Once officers are registered, coordinate an orientation and begin using Police Narratives AI during the agreed-upon trial period.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-32">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
          <Logo className="h-8 w-auto" aria-label="Police Narratives AI" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
            Trial participation
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {loading && (
          <div className="flex justify-center py-24">
            <span className="loading loading-spinner loading-lg text-blue-500" />
          </div>
        )}

        {!loading && !info && (
          <GlassCard interactive={false} className="mx-auto max-w-lg p-8 text-center">
            <h1 className="font-display text-2xl font-bold m-0">We couldn't open this invitation</h1>
            <p className="mt-3 text-slate-600">{error}</p>
            <p className="mt-2 text-sm text-slate-500">
              Contact your Police Narratives AI representative or email{" "}
              <a className="text-blue-600" href="mailto:support@policenarratives.ai">
                support@policenarratives.ai
              </a>
              .
            </p>
          </GlassCard>
        )}

        {info && (
          <>
            <h1 className="font-display text-5xl font-bold leading-tight m-0">
              Trial participation <SerifAccent className="text-blue-600">requirements</SerifAccent>
            </h1>
            <p className="mt-3 text-slate-500">
              A structured trial designed to produce clear, real-world results
            </p>

            <div className="mt-8 grid gap-6 rounded-2xl bg-blue-50 p-6 sm:grid-cols-3">
              <div>
                <SectionLabel className="!text-[11px]">Prepared for</SectionLabel>
                <p className="mt-1 break-all font-medium">{info.email}</p>
              </div>
              <div>
                <SectionLabel className="!text-[11px]">Your trial</SectionLabel>
                <p className="mt-1 text-sm text-slate-700">
                  {seatSummary}, for {info.trialDays} days from signup
                </p>
              </div>
              <div>
                <SectionLabel className="!text-[11px]">Support model</SectionLabel>
                <p className="mt-1 text-sm text-slate-700">
                  Training, adoption reviews & legal coordination
                </p>
              </div>
            </div>

            <section className="mt-12">
              <SectionLabel>Trial objective</SectionLabel>
              <h2 className="font-display text-2xl font-bold mt-1">
                What the trial is designed to accomplish
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                We appreciate the opportunity to serve your department during the
                agreed-upon trial period. Our mission is to save officers time,
                improve the quality and consistency of reports, and allow officers
                to spend more time in the field and less time completing paperwork.
              </p>
              <SectionLabel className="mt-6 !text-[11px]">Why the process matters</SectionLabel>
              <div className="mt-2 grid gap-6 text-slate-600 leading-relaxed md:grid-cols-2">
                <p className="m-0">
                  Through our work with departments of varying sizes and needs, we
                  have established a structured approach to running product trials.
                  When trials are conducted according to these standards, they
                  provide clear, actionable insight into whether the solution
                  delivers the outcomes your department is seeking.
                </p>
                <p className="m-0">
                  To ensure the trial is productive, efficient, and respectful of
                  both your department's time and ours, and that participating
                  officers are set up for success in maximizing the utilization of
                  Police Narratives AI, we ask that the following expectations be
                  met. These touchpoints allow us to provide the highest level of
                  support and ensure the trial produces meaningful, real-world
                  results.
                </p>
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                n="01"
                title="Department requirements"
                intro="The following participation standards help keep the trial aligned, supported, and measurable."
              />
              <div className="grid gap-4 md:grid-cols-2">
                {REQUIREMENTS.map((i) => (
                  <ItemCard key={i.title} item={i} />
                ))}
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                n="02"
                title="Legal"
                intro="The trial should be evaluated within the department's existing prosecutorial, security, records, and review framework. The following safeguards apply specifically to trial activity."
              />
              <div className="grid gap-4 md:grid-cols-2">
                {SAFEGUARDS.map((i) => (
                  <ItemCard key={i.title} item={i} />
                ))}
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                n="03"
                title="Police Narratives AI will provide"
                intro="Support is structured around adoption, training, troubleshooting, and measurable usage throughout the trial."
              />
              <div className="grid gap-4 md:grid-cols-2">
                {PROVIDES.map((i) => (
                  <ItemCard key={i.title} item={i} />
                ))}
              </div>
            </section>

            <section className="mt-14">
              <SectionHeading
                n="04"
                title="How to start your trial"
                intro="Follow the steps below to register your department and onboard trial officers."
              />
              <ol className="m-0 list-none space-y-3 p-0">
                {steps.map((s, idx) => (
                  <li key={s.title} className="flex gap-4 rounded-2xl bg-blue-50 p-5">
                    <span className="font-display text-lg font-bold text-blue-500">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="m-0 font-semibold">{s.title}</p>
                      <p className="m-0 mt-1 text-sm leading-relaxed text-slate-600">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-6 rounded-xl border border-blue-200 p-4 text-sm text-slate-700">
                <SectionLabel className="!text-[11px]">Important: officer invitations</SectionLabel>
                <p className="m-0 mt-1">
                  Participating officers must register through the invitation link
                  sent to their email. Signing up independently may prevent their
                  accounts from being associated with the department's free trial.
                </p>
              </div>
            </section>
          </>
        )}
      </main>

      {info && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-6 py-4">
            <p className="m-0 text-sm text-slate-600">
              By continuing, your department agrees to the trial participation
              requirements above.
            </p>
            <div className="flex items-center gap-3">
              {error && <span className="text-sm text-red-500">{error}</span>}
              <Button variant="glow" onClick={agree} disabled={submitting}>
                {submitting ? "One moment..." : "Agree and create my account"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialRequirements;
